import React, { useEffect, useRef, useState } from "react";
import { FiMic, FiSquare, FiTrash2, FiRotateCcw } from "react-icons/fi";
import { HiOutlineCheckCircle } from "react-icons/hi2";

import { useMediaRecorder } from "../../lib/useAudioRecorder";
import AudioVisualizer from "../../lib/AudioVisualizer";
import Media from "../shared/Media";
import PostButton from "../PostButton";
import ToastMessage from "../ToastMessage";

import { createComment, createReply } from "../../api";

const MAX_DURATION = 15;

const VoiceComposer = ({ mode, postId, postType, type, comment, close, closeReply }) => {
  const [content, setContent] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const timerRef = useRef(null);
  const uniqueIdRef = useRef(`voice-${mode}-${Date.now()}`);

  const isReply = mode === "reply";

  const { mediaUrl, link, startRecording, stopRecording, deleteRecording, recordingState, analyser } = useMediaRecorder();

  useEffect(() => {
    if (mediaUrl) setContent(mediaUrl);
  }, [mediaUrl]);

  const clearTimer = () => {
    if (!timerRef.current) return;

    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  useEffect(() => {
    if (recordingState !== "recording") {
      clearTimer();
      return;
    }

    setRecordingTime(0);

    timerRef.current = setInterval(() => {
      setRecordingTime((previous) => {
        const next = previous + 1;

        if (next >= MAX_DURATION) {
          setTimeout(() => stopRecording(), 0);
          return MAX_DURATION;
        }

        return next;
      });
    }, 1000);

    return clearTimer;
  }, [recordingState, stopRecording]);

  useEffect(() => {
    return () => clearTimer();
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secs = Math.floor(seconds % 60).toString().padStart(2, "0");

    return `${mins}:${secs}`;
  };

  const handleStartRecording = () => {
    setContent("");
    setRecordingTime(0);
    startRecording();
  };

  const handleStopRecording = () => {
    clearTimer();
    stopRecording();
  };

  const handleDelete = () => {
    clearTimer();
    deleteRecording();

    setContent("");
    setRecordingTime(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isReply && !comment?.id) {
      ToastMessage("Comment ID is missing.");
      return;
    }

    if (!content || !link) {
      ToastMessage(isReply ? "Please record something first." : "Please record a voice comment first.");
      return;
    }

    if (isSubmitting) return;

    const mimeType = link.type || "audio/webm";
    let extension = "webm";

    if (mimeType.includes("mp4")) extension = "mp4";
    else if (mimeType.includes("mpeg")) extension = "mp3";
    else if (mimeType.includes("ogg")) extension = "ogg";

    const audioFile = new File([link], `voice-${isReply ? "reply" : "comment"}-${Date.now()}.${extension}`, { type: mimeType });
    const formData = new FormData();

    formData.append("type", isReply ? type : postType);

    if (isReply) formData.append("comment_id", comment.id);
    else formData.append("post_id", postId);

    formData.append("content", audioFile, audioFile.name);

    try {
      setIsSubmitting(true);

      const res = isReply ? await createReply(formData) : await createComment(formData);

      if (res?.data?.message) {
        ToastMessage(res.data.message);
        handleDelete();

        if (isReply) closeReply?.();
        else close?.();
      }
    } catch (error) {
      console.error(isReply ? "Voice reply error:" : "Voice comment error:", error);

      ToastMessage(error?.response?.data?.message || error?.message || (isReply ? "Failed to create voice reply." : "Unable to create your voice comment."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const remainingTime = Math.max(MAX_DURATION - recordingTime, 0);

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {/* ================= IDLE ================= */}

        {!mediaUrl && recordingState === "idle" && (
          <div className="flex min-h-[300px] flex-col items-center justify-center px-6 py-8 text-center">
            <div className="relative">
              <div className="absolute inset-0 scale-[1.55] rounded-full bg-purple-50" />
              <div className="absolute inset-0 scale-[1.25] rounded-full bg-purple-100" />

              <button type="button" onClick={handleStartRecording} className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-purple-600 text-white shadow-lg shadow-purple-500/20 transition-all duration-200 hover:scale-105 hover:bg-purple-700 active:scale-95">
                <FiMic size={26} />
              </button>
            </div>

            <h3 className="mt-9 text-base font-semibold text-gray-900">Record a voice {isReply ? "reply" : "comment"}</h3>

            <p className="mt-2 max-w-[280px] text-sm leading-5 text-gray-400">
              {isReply ? "Tap the microphone and record your response." : "Tap the microphone and share what’s on your mind."}
            </p>

            <div className="mt-6 rounded-full bg-gray-50 px-4 py-2 text-xs text-gray-400">Maximum {MAX_DURATION} seconds</div>
          </div>
        )}

        {/* ================= RECORDING ================= */}

        {recordingState === "recording" && (
          <div className="px-5 py-6">
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-500">
                  <FiMic size={19} />
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Recording</h3>
                  <p className="mt-0.5 text-xs text-gray-400">Your microphone is active</p>
                </div>
              </div>

              <div className="flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-500">
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                LIVE
              </div>
            </div>

            {/* TIMER */}

            <div className="mb-5 flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
              <span className="text-sm font-semibold tabular-nums text-gray-700">{formatTime(recordingTime)} / 00:15</span>
              <span className={`text-xs font-medium ${remainingTime <= 5 ? "text-red-500" : "text-gray-400"}`}>{remainingTime}s left</span>
            </div>

            {/* PROGRESS */}

            <div className="mb-5 h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-purple-600 transition-all duration-1000" style={{ width: `${(recordingTime / MAX_DURATION) * 100}%` }} />
            </div>

            {/* VISUALIZER */}

            <div className="mb-7 flex min-h-[110px] items-center rounded-2xl border border-gray-100 bg-gray-50/70 px-4 py-5">
              {analyser && <AudioVisualizer analyser={analyser} isPlaying={recordingState === "recording"} mediaUrl={null} />}
            </div>

            {/* STOP */}

            <div className="flex flex-col items-center">
              <button type="button" onClick={handleStopRecording} className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500 text-white shadow-lg shadow-red-500/20 transition-all hover:scale-105 hover:bg-red-600 active:scale-95">
                <FiSquare size={18} />
              </button>

              <span className="mt-3 text-xs font-medium text-gray-400">{recordingTime >= MAX_DURATION ? "Maximum reached" : "Tap to stop"}</span>
            </div>
          </div>
        )}

        {/* ================= READY ================= */}

        {mediaUrl && (
          <div className="p-5">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Voice {isReply ? "reply" : "comment"} ready</h3>
                <p className="mt-1 text-xs text-gray-400">Preview it before posting.</p>
              </div>

              <div className="flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-600">
                <HiOutlineCheckCircle size={16} />
                Ready
              </div>
            </div>

            {/* AUDIO PLAYER */}

            <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4">
              <Media media={mediaUrl} container={uniqueIdRef.current} />
            </div>

            {/* ACTIONS */}

            <div className="mt-4 flex items-center gap-3">
              <button type="button" onClick={handleDelete} disabled={isSubmitting} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:border-purple-200 hover:bg-purple-50 hover:text-purple-600 disabled:opacity-50">
                <FiRotateCcw size={16} />
                Record again
              </button>

              <button type="button" onClick={handleDelete} disabled={isSubmitting} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-400 transition hover:border-red-100 hover:bg-red-50 hover:text-red-500 disabled:opacity-50">
                <FiTrash2 size={17} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ================= CREATE / REPLY ================= */}

      {mediaUrl && (
        <div className="mt-4">
          <PostButton type="submit" text={isSubmitting ? "Sending..." : isReply ? "Reply" : undefined} disabled={isSubmitting || !link} />
        </div>
      )}
    </form>
  );
};

export default VoiceComposer;